import { useCallback, useState } from "react";

// Per-project column width + visibility state. Persists both maps to
// localStorage so the user's layout survives reloads on each project.
export default function useColumnPreferences(projectId) {
  const LS_WIDTHS = `pg_colwidths_${projectId}`;
  const LS_HIDDEN = `pg_hiddencols_${projectId}`;

  const [colWidths, setColWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_WIDTHS);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_HIDDEN);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  const persistWidths = useCallback(
    (next) => {
      setColWidths(next);
      try { localStorage.setItem(LS_WIDTHS, JSON.stringify(next)); } catch {}
    },
    [LS_WIDTHS],
  );
  const persistHidden = useCallback(
    (next) => {
      setHiddenCols(next);
      try { localStorage.setItem(LS_HIDDEN, JSON.stringify(Array.from(next))); } catch {}
    },
    [LS_HIDDEN],
  );

  const resizeColumn = useCallback(
    (key, w) => persistWidths({ ...colWidths, [key]: w }),
    [colWidths, persistWidths],
  );
  const toggleColumnVisibility = useCallback(
    (key) => {
      const next = new Set(hiddenCols);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      persistHidden(next);
    },
    [hiddenCols, persistHidden],
  );
  const showAllColumns = useCallback(() => persistHidden(new Set()), [persistHidden]);
  const resetColumnWidths = useCallback(() => persistWidths({}), [persistWidths]);

  return {
    colWidths,
    hiddenCols,
    resizeColumn,
    toggleColumnVisibility,
    showAllColumns,
    resetColumnWidths,
    // Escape hatches for saved-views apply/restore:
    setColWidths: persistWidths,
    setHiddenCols: persistHidden,
  };
}
